import { 
  query as fbQuery, 
  collection, 
  collectionGroup, 
  getDoc, 
  getDocs, 
  limit,
  onSnapshot,
  orderBy,
  startAfter,
  where,
  doc
} from "firebase/firestore";
import { 
  db, 
  rtDb 
} from "./firebase.utils";
import { 
  query as rtQuery, 
  ref, 
  orderByChild, 
  equalTo, 
  get, 
  onChildChanged
} from "firebase/database";

// Retrieves a unique document from Firestore
export async function getUniqueDocumentFromFirestore(e) {
  let docRef;

  if (e.path) {
    docRef = doc(db, e.path);
  }

  if (e.once) {
    getDoc(docRef).then(doc => {
      if (doc.exists) {
        const data = doc.data();
        e.callback(data);
      }
    });
    return;
  }

  const unsubscribe = onSnapshot(docRef, doc => {
    const data = doc.data();
    e.callback(data, doc.id)
  });
  e.handleListener(unsubscribe);

}

// Retrieves a collection from Firestore
export async function getCollectionFromFirestore(e) {
  let collectionRef;

  if (e.path && e.collectionGroup) {
    collectionRef = collectionGroup(db, e.path);
  }
  else if (e.path) {
    collectionRef = collection(db, e.path);
  }

  if (e.once) {
    const docObj = {};
    getDocs(collectionRef).then(snapshot => {
      snapshot.forEach(doc => {
        docObj[doc.id] = doc.data();
      });
      e.callback(docObj);
    });
    
    return;
  }

  const unsubscribe = onSnapshot(collectionRef, snapshot => {
    snapshot.forEach(doc => {
      const data = doc.data();
      e.callback(data, doc.id);
    })
  });
  e.handleListener(unsubscribe);
}

// Retrieves documents from Firestore based on query parameters
export async function getQueryFromFirestore(e) {
  let queryRef;
  let whereArr = [];
  if (e.queryArray) {
    e.queryArray.forEach(q => {
      whereArr.push(where(q.field, q.operator, q.value));
    });
  }

  if (e.loadLimit) {
    whereArr.push(limit(e.loadLimit));
  }

  if (e.orderBy) {
    whereArr.push(e.orderBy.direction === 'desc' 
      ?
      orderBy(e.orderBy.field, 'desc') 
      :
      orderBy(e.orderBy.field, 'asc')
    );
  }

  if (e.path && e.collectionGroup) {
    queryRef = fbQuery(collectionGroup(db, e.path), ...whereArr);
  }
  else if (e.path) {
    queryRef = fbQuery(collection(db, e.path), ...whereArr);
  }

  if (e.once) {
    const docObj = {};
    getDocs(queryRef).then(snapshot => {
      snapshot.forEach(doc => {
        docObj[doc.id] = doc.data();
      });
      e.callback(docObj);
    });
    return;
  }

  const unsubscribe = onSnapshot(queryRef, snapshot => {
    snapshot.forEach(doc => {
      e.callback(doc.data(), doc.id);
    });
  });
  e.handleListener(unsubscribe);
}

// Retrieves a page of documents from Firestore with cursor-based pagination.
// Returns a promise resolving to { data: [...docData], lastDoc, hasMore }.
// Pass the returned `lastDoc` back in as `e.startAfterDoc` to fetch the next page.
export async function getPaginatedQueryFromFirestore(e) {
  const constraints = [];

  if (e.queryArray) {
    e.queryArray.forEach(q => {
      constraints.push(where(q.field, q.operator, q.value));
    });
  }

  if (e.orderBy) {
    constraints.push(orderBy(
      e.orderBy.field,
      e.orderBy.direction === 'desc' ? 'desc' : 'asc'
    ));
  }

  if (e.startAfterDoc) {
    constraints.push(startAfter(e.startAfterDoc));
  }

  const loadLimit = e.loadLimit || 20;
  constraints.push(limit(loadLimit));

  const collectionRef = e.collectionGroup
    ? collectionGroup(db, e.path)
    : collection(db, e.path);
  const queryRef = fbQuery(collectionRef, ...constraints);

  const snapshot = await getDocs(queryRef);
  const data = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

  return {
    data,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === loadLimit
  };
}

export async function getQueryFromRtDb(e) {
  let queryRef;

  if (e.query) {
    queryRef = rtQuery(
      ref(rtDb, e.path), 
      orderByChild(e.query.key), 
      equalTo(e.query.value)
    );
  } else if (e.path) {
    queryRef = ref(rtDb, e.path);
  }

  get(queryRef).then(snap => {
    if (snap.exists()) {
      const data = snap.val();
      e.callback(data);
    } else {
      e.callback(null);
    }
  });

  if (!e.once) {
    onChildChanged(queryRef, snap => {
      const data = snap.val();
      e.callback({ [snap.key]: data });
    });
    e.handleListener(queryRef);
  }
}